import React, { useState } from 'react';
import { FaEnvelope, FaCalendarAlt, FaUser, FaPaperPlane, FaFileAlt, FaUndo, FaCheckCircle } from 'react-icons/fa';

const TransmissionErrorForm = ({ setStep, onSubmit }) => {
  const [formData, setFormData] = useState({
    dateDiscovered: '',
    timeDiscovered: '',
    discoveredBy: '',
    patientsAffected: '',
    transmissionMethod: '',
    intendedRecipient: '',
    actualRecipient: '',
    phiTypes: [],
    incidentDescription: '',
    causeOfError: '',
    immediateActions: '',
    recipientContacted: false,
    recipientResponse: '',
    phiRecovered: false,
    recoveryMethod: '',
    patientNotified: false,
    patientNotificationDate: '',
    riskLevel: 'low',
    preventiveMeasures: '',
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const transmissionMethods = [
    'Fax',
    'Email',
    'Regular Mail',
    'Certified Mail',
    'Secure Message',
    'Electronic System',
    'Hand Delivery',
    'Other'
  ];

  const phiTypeOptions = [
    'Patient Names',
    'Medical Records',
    'Lab Results',
    'Imaging Results',
    'Treatment Plans',
    'Billing Information',
    'Insurance Information',
    'Contact Information',
    'Other PHI'
  ];

  const causeOptions = [
    'Wrong fax number',
    'Wrong email address',
    'Wrong mailing address',
    'System error',
    'Human error',
    'Auto-complete error',
    'Similar names confusion',
    'Other'
  ];

  const riskLevels = [
    { value: 'low', label: 'Low - Minimal risk', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium - Some risk', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High - Significant risk', color: 'bg-orange-100 text-orange-800' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleMultiSelectChange = (e, fieldName) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked 
        ? [...prev[fieldName], value]
        : prev[fieldName].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.dateDiscovered) newErrors.dateDiscovered = 'Date discovered is required';
    if (!formData.discoveredBy.trim()) newErrors.discoveredBy = 'Discovered by is required';
    if (!formData.patientsAffected) newErrors.patientsAffected = 'Number of patients is required';
    if (!formData.transmissionMethod) newErrors.transmissionMethod = 'Transmission method is required';
    if (!formData.actualRecipient.trim()) newErrors.actualRecipient = 'Actual recipient is required';
    if (!formData.incidentDescription.trim()) newErrors.incidentDescription = 'Description is required';
    if (!formData.reportedBy.trim()) newErrors.reportedBy = 'Reporter name is required';
    if (!formData.reportedByEmail.trim()) newErrors.reportedByEmail = 'Reporter email is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await onSubmit({
        ...formData,
        formType: 'HIPAADisclosure',
        subType: 'TransmissionError',
        submittedAt: new Date(),
        severity: formData.riskLevel
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    const riskObj = riskLevels.find(r => r.value === risk);
    return riskObj ? riskObj.color : 'bg-green-100 text-green-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaEnvelope className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Transmission Error Report
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report PHI sent to wrong recipient via fax, email, or mail
          </p>
          <div className={`mt-4 inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${getRiskColor(formData.riskLevel)}`}>
            Risk Level: {formData.riskLevel.charAt(0).toUpperCase() + formData.riskLevel.slice(1)}
          </div>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and how the transmission error was discovered
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Date Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateDiscovered"
                        name="dateDiscovered"
                        type="date"
                        value={formData.dateDiscovered}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.dateDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.dateDiscovered && <p className="mt-1 text-sm text-red-600">{errors.dateDiscovered}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="timeDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Time Discovered
                    </label>
                    <div className="mt-2">
                      <input
                        id="timeDiscovered"
                        name="timeDiscovered"
                        type="time"
                        value={formData.timeDiscovered}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="discoveredBy" className="block text-sm/6 font-medium text-gray-900">
                      Discovered By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="discoveredBy"
                        name="discoveredBy"
                        type="text"
                        value={formData.discoveredBy}
                        onChange={handleInputChange}
                        placeholder="Name and title"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.discoveredBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.discoveredBy && <p className="mt-1 text-sm text-red-600">{errors.discoveredBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="patientsAffected" className="block text-sm/6 font-medium text-gray-900">
                      Patients Affected <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="patientsAffected"
                        name="patientsAffected"
                        type="number"
                        value={formData.patientsAffected}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.patientsAffected ? 'outline-red-500' : ''}`}
                      />
                      {errors.patientsAffected && <p className="mt-1 text-sm text-red-600">{errors.patientsAffected}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm/6 font-medium text-gray-900 mb-4">Risk Assessment</label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      {riskLevels.map(risk => (
                        <div key={risk.value} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                              <input
                                type="radio"
                                name="riskLevel"
                                value={risk.value}
                                checked={formData.riskLevel === risk.value}
                                onChange={handleInputChange}
                                className="col-start-1 row-start-1 appearance-none rounded-full border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                              />
                              <div className="pointer-events-none col-start-1 row-start-1 size-2 self-center justify-self-center rounded-full bg-white opacity-0 group-has-[:checked]:opacity-100" />
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{risk.label}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Transmission Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaPaperPlane className="size-5 text-gray-400" />
                  Transmission Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  How and to whom PHI was transmitted
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="transmissionMethod" className="block text-sm/6 font-medium text-gray-900">
                      Transmission Method <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <select
                        id="transmissionMethod"
                        name="transmissionMethod"
                        value={formData.transmissionMethod}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.transmissionMethod ? 'outline-red-500' : ''}`}
                      >
                        <option value="">Select method...</option>
                        {transmissionMethods.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                      {errors.transmissionMethod && <p className="mt-1 text-sm text-red-600">{errors.transmissionMethod}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="causeOfError" className="block text-sm/6 font-medium text-gray-900">
                      Cause of Error
                    </label>
                    <div className="mt-2">
                      <select
                        id="causeOfError"
                        name="causeOfError"
                        value={formData.causeOfError}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      >
                        <option value="">Select cause...</option>
                        {causeOptions.map(cause => (
                          <option key={cause} value={cause}>{cause}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="intendedRecipient" className="block text-sm/6 font-medium text-gray-900">
                      Intended Recipient
                    </label>
                    <div className="mt-2">
                      <input
                        id="intendedRecipient"
                        name="intendedRecipient"
                        type="text"
                        value={formData.intendedRecipient}
                        onChange={handleInputChange}
                        placeholder="Who was supposed to receive the PHI?"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="actualRecipient" className="block text-sm/6 font-medium text-gray-900">
                      Actual Recipient <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="actualRecipient"
                        name="actualRecipient"
                        type="text"
                        value={formData.actualRecipient}
                        onChange={handleInputChange}
                        placeholder="Who actually received the PHI?"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.actualRecipient ? 'outline-red-500' : ''}`}
                      />
                      {errors.actualRecipient && <p className="mt-1 text-sm text-red-600">{errors.actualRecipient}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm/6 font-medium text-gray-900 mb-4">Types of PHI Transmitted</label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {phiTypeOptions.map(phiType => (
                        <div key={phiType} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                              <input
                                type="checkbox"
                                value={phiType}
                                checked={formData.phiTypes.includes(phiType)}
                                onChange={(e) => handleMultiSelectChange(e, 'phiTypes')}
                                className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                              />
                              <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                                <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                              </svg>
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{phiType}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Description */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileAlt className="size-5 text-gray-400" />
                  Incident Description
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Detailed description of the transmission error
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="incidentDescription" className="block text-sm/6 font-medium text-gray-900">
                      Detailed Description <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="incidentDescription"
                        name="incidentDescription"
                        rows={4}
                        value={formData.incidentDescription}
                        onChange={handleInputChange}
                        placeholder="Describe exactly what happened and how the error occurred..."
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentDescription ? 'outline-red-500' : ''}`}
                      />
                      {errors.incidentDescription && <p className="mt-1 text-sm text-red-600">{errors.incidentDescription}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="immediateActions" className="block text-sm/6 font-medium text-gray-900">
                      Immediate Actions Taken
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="immediateActions"
                        name="immediateActions"
                        rows={3}
                        value={formData.immediateActions}
                        onChange={handleInputChange}
                        placeholder="What steps were taken immediately after discovering the error?"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Recovery Actions */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUndo className="size-5 text-gray-400" />
                  Recovery Actions
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Actions taken to recover or secure the misdirected PHI
                </p>

                <div className="mt-10 space-y-6">
                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="recipientContacted"
                          checked={formData.recipientContacted}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Unintended recipient was contacted</label>
                  </div>

                  {formData.recipientContacted && (
                    <div>
                      <label htmlFor="recipientResponse" className="block text-sm/6 font-medium text-gray-900">
                        Recipient Response
                      </label>
                      <div className="mt-2">
                        <textarea
                          id="recipientResponse"
                          name="recipientResponse"
                          rows={3}
                          value={formData.recipientResponse}
                          onChange={handleInputChange}
                          placeholder="How did the recipient respond? Did they agree to return/destroy the PHI?"
                          className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="phiRecovered"
                          checked={formData.phiRecovered}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">PHI was recovered/destroyed</label>
                  </div>

                  {formData.phiRecovered && (
                    <div>
                      <label htmlFor="recoveryMethod" className="block text-sm/6 font-medium text-gray-900">
                        Recovery Method
                      </label>
                      <div className="mt-2">
                        <input
                          id="recoveryMethod"
                          name="recoveryMethod"
                          type="text"
                          value={formData.recoveryMethod}
                          onChange={handleInputChange}
                          placeholder="How was the PHI recovered or destroyed?"
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="patientNotified"
                          checked={formData.patientNotified}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Patient(s) were notified</label>
                  </div>

                  {formData.patientNotified && (
                    <div>
                      <label htmlFor="patientNotificationDate" className="block text-sm/6 font-medium text-gray-900">
                        Patient Notification Date
                      </label>
                      <div className="mt-2">
                        <input
                          id="patientNotificationDate"
                          name="patientNotificationDate"
                          type="date"
                          value={formData.patientNotificationDate}
                          onChange={handleInputChange}
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="preventiveMeasures" className="block text-sm/6 font-medium text-gray-900">
                      Preventive Measures
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="preventiveMeasures"
                        name="preventiveMeasures"
                        rows={3}
                        value={formData.preventiveMeasures}
                        onChange={handleInputChange}
                        placeholder="What measures will prevent similar transmission errors?"
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Reporter Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Your contact information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reportedBy" className="block text-sm/6 font-medium text-gray-900">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedBy"
                        name="reportedBy"
                        type="text"
                        value={formData.reportedBy}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedBy && <p className="mt-1 text-sm text-red-600">{errors.reportedBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reportedByTitle" className="block text-sm/6 font-medium text-gray-900">
                      Your Title
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByTitle"
                        name="reportedByTitle"
                        type="text"
                        value={formData.reportedByTitle}
                        onChange={handleInputChange}
                        placeholder="Your job title"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="reportedByEmail" className="block text-sm/6 font-medium text-gray-900">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByEmail"
                        name="reportedByEmail"
                        type="email"
                        value={formData.reportedByEmail}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByEmail ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByEmail && <p className="mt-1 text-sm text-red-600">{errors.reportedByEmail}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransmissionErrorForm;
